import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { DropdownChangeEvent } from '@volvo/vcdk';
import { Button, Card, CardContent, Dropdown, DropdownOption, Typography } from '@volvo/vcdk-react';
import { useState } from 'react';
import { z } from 'zod';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import { fleetAccountsQueryOptions } from '@/service/vpc/queryOptions';

const selectFleetSchema = z.object({
  register: z.coerce.boolean().optional(),
  sn: z.string().optional(),
  guid: z.string().optional(),
  origin: z.string().optional(),
});

export const Route = createFileRoute('/_authenticated/select-fleet')({
  component: SelectFleetPage,
  errorComponent: ErrorBoundary,
  validateSearch: selectFleetSchema,
});

function SelectFleetPage() {
  const navigate = useNavigate();
  const { setFleetAccountId } = useFleetAccount();
  const { register, sn, guid, origin } = Route.useSearch();

  const [value, setValue] = useState<string>();
  const { data } = useQuery(fleetAccountsQueryOptions());

  const onChange = (e: DropdownChangeEvent) => {
    setValue(() => e.detail.value);
  };

  const handleContinue = () => {
    if (value) {
      setFleetAccountId(value);

      // Check if all required registration params are present
      const hasValidRegistrationParams = register && sn && guid && origin;
      if (hasValidRegistrationParams) {
        navigate({
          to: '/units',
          search: { register, sn, guid, origin },
        });
      } else {
        navigate({ to: '/dashboard' });
      }
    }
  };

  return (
    <div className="fixed inset-0 z-20 flex flex-col items-center justify-center gap-4">
      <img
        src={`${import.meta.env.VITE_BASE_PATH}images/about-at-sea-main.webp`}
        alt="About at Sea"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="relative z-10 w-full max-w-[420px]">
        <Card>
          <CardContent>
            <Typography variant="heading3" className="mb-6">
              Welcome to Volvo Penta Connect
            </Typography>
            <Typography variant="body" className="mb-6">
              You are connected to multiple fleet accounts. Please choose one below:
            </Typography>
            <Dropdown
              onChange={onChange}
              size="medium"
              value={value}
              placeholder="Select a fleet account"
              placement="bottom"
              className="[&::part(input-wrapper)]:h-10"
            >
              {data?.map((option, option_index) => (
                <DropdownOption key={option_index} value={option.fleetAccountId}>
                  {option.name}
                </DropdownOption>
              ))}
            </Dropdown>
            <Button variant="primary" disabled={!value} className="mt-6 w-full" onClick={handleContinue}>
              Continue
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
